"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlayerOptions = exports.PlayerDriverOptions = exports.ManifestOptions = exports.FlvHttpDriverOptions = exports.FlvHttpDriverFull = exports.FlvHttpDriverID = exports.Mp4WsDriverOptions = exports.Mp4WsDriverFull = exports.Mp4WsDriverID = exports.NativeHlsDriverOptions = exports.NativeHlsDriverFull = exports.NativeHlsDriverID = exports.HlsjsDriverOptions = exports.HlsjsDriverFull = exports.HlsjsDriverID = exports.WebrtcDriverOptions = exports.WebrtcDriverFull = exports.WebrtcDriverID = void 0;
const zod_1 = require("zod");
const adapter_1 = require("./adapter");
const bitrate_switching_1 = require("./player/features/bitrate-switching");
// helper to mark deprecated options
// should be used in .superRefine(deprecated("reason"))
function deprecated(desc) {
    return (val, ctx) => {
        if (val != null && adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING)) {
            adapter_1.device.console.warn(`[deprecated] ${ctx.path.join(".")} is deprecated: ${desc}`);
        }
    };
}
//#region Common Options
const Common = zod_1.z.object({
    timeout: zod_1.z.number().min(1000).max(30000).default(10000).describe("timeout to start player in ms"),
    // deprecated ones
    bitrate: zod_1.z.number().optional().superRefine(deprecated("use 'preferredLevel' from root options instead")),
    muted: zod_1.z.boolean().optional().superRefine(deprecated("use 'muted' from root options instead")),
    volume: zod_1.z.number().optional().superRefine(deprecated("use 'volume' from root options instead")),
    autoPlay: zod_1.z.boolean().optional().superRefine(deprecated("use 'autoPlay' from root options instead")),
    preset: zod_1.z.null().optional().superRefine(deprecated("use 'preferredLevel' from root options instead")),
});
//#endregion
//#region Webrtc Options
exports.WebrtcDriverID = zod_1.z.literal("webrtc");
exports.WebrtcDriverFull = Common.extend({
    id: exports.WebrtcDriverID,
    joinLoadbalancerFallback: zod_1.z.boolean().default(true).describe("Fallback to loadbalancer if direct connection fails"),
});
exports.WebrtcDriverOptions = exports.WebrtcDriverID.or(exports.WebrtcDriverFull).
    transform((input) => typeof input === "string" ? exports.WebrtcDriverFull.parse({ id: input }) : input);
//#endregion
//#region Hlsjs Options
exports.HlsjsDriverID = zod_1.z.literal("hlsjs");
exports.HlsjsDriverFull = Common.extend({
    id: exports.HlsjsDriverID,
    hlsjsConfig: zod_1.z.object({
        debug: zod_1.z.boolean().default(false).describe("Enable hls.js debug mode"),
        maxBufferLength: zod_1.z.number().default(30).describe("Maximum buffer length in seconds"),
        maxBufferSize: zod_1.z.number().default(60000000).describe("Maximum buffer size in bytes"),
        fragLoadingTimeOut: zod_1.z.number().default(3900).describe("Fragment loading timeout in milliseconds"),
        maxBufferHole: zod_1.z.number().
            default(2).
            describe("'Maximum' inter-fragment buffer hole tolerance that hls.js can cope with when searching for the next fragment to load"),
        backBufferLength: zod_1.z.number().
            default(90).
            describe("The maximum duration of buffered media to keep once it has been played, in seconds"),
        progressive: zod_1.z.boolean().default(false).describe("Enable streaming segment data with fetch loader (experimental)"),
        startLevel: zod_1.z.number().default(-1).describe("When set, use this level as the default hls.startLevel"),
    }).
        passthrough().
        optional().
        describe("Fine-tuning hls.js config from https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning"),
    hlsjsPath: zod_1.z.string().optional().describe("Path to custom hls.js library"),
    // deprecated ones
    estimatedKbps: zod_1.z.number().optional().superRefine(deprecated("use 'preferredLevel' from root options instead")),
    maxBufferSize: zod_1.z.number().optional().superRefine(deprecated("use 'hlsjsConfig.maxBufferSize' instead")),
    maxBufferLength: zod_1.z.number().optional().superRefine(deprecated("use 'hlsjsConfig.maxBufferLength' instead")),
    debug: zod_1.z.boolean().optional().superRefine(deprecated("use 'hlsjsConfig.debug' instead")),
});
exports.HlsjsDriverOptions = exports.HlsjsDriverID.or(exports.HlsjsDriverFull).
    transform((input) => typeof input === "string" ? exports.HlsjsDriverFull.parse({ id: input }) : input);
//#endregion
//#region NativeHls Options
exports.NativeHlsDriverID = zod_1.z.literal("native-hls");
exports.NativeHlsDriverFull = Common.extend({
    id: exports.NativeHlsDriverID,
});
exports.NativeHlsDriverOptions = exports.NativeHlsDriverID.or(exports.NativeHlsDriverFull).
    transform((input) => typeof input === "string" ? exports.NativeHlsDriverFull.parse({ id: input }) : input);
//#endregion
//#region Mp4Ws Options
exports.Mp4WsDriverID = zod_1.z.literal("mp4ws");
exports.Mp4WsDriverFull = Common.extend({
    id: exports.Mp4WsDriverID,
    maxShifts: zod_1.z.number().optional(),
    requiredBuffer: zod_1.z.number().optional(),
    requiredBufferBeforeRemove: zod_1.z.number().optional(),
    backFill: zod_1.z.number().optional(),
    maxGap: zod_1.z.number().optional(),
    retry: zod_1.z.number().optional(),
    mode: zod_1.z.enum(["N", "B", "N"]).optional(),
    download: zod_1.z.boolean().optional(),
    segmentDuration: zod_1.z.number().optional(),
    initFailTimeout: zod_1.z.number().optional(),
    playbackFailTimeout: zod_1.z.number().optional(),
    skipWatchInterval: zod_1.z.number().optional(),
    skipForwardThreshold: zod_1.z.number().optional(),
    bitrate: zod_1.z.number().optional(),
    estimatedKbps: zod_1.z.number().optional(),
    origin: zod_1.z.boolean().optional(),
});
exports.Mp4WsDriverOptions = exports.Mp4WsDriverID.or(exports.Mp4WsDriverFull).
    transform((input) => typeof input === "string" ? exports.Mp4WsDriverFull.parse({ id: input }) : input);
//#endregion
//#region FlvHttp Options
exports.FlvHttpDriverID = zod_1.z.literal("flvhttp");
exports.FlvHttpDriverFull = Common.extend({
    id: exports.FlvHttpDriverID,
});
exports.FlvHttpDriverOptions = exports.FlvHttpDriverID.or(exports.FlvHttpDriverFull).
    transform((input) => typeof input === "string" ? exports.FlvHttpDriverFull.parse({ id: input }) : input);
//#endregion
//#region Manifest Options
const ManifestPollingOptions = zod_1.z.object({
    default: zod_1.z.number().default(5000).describe("Manifest polling interval in ms"),
    unauthorized: zod_1.z.number().default(2000).describe("Manifest polling interval in ms for 401, 403 codes"),
    notfound: zod_1.z.number().default(10000).describe("Manifest polling interval in ms for 404 code"),
});
exports.ManifestOptions = zod_1.z.object({
    polling: ManifestPollingOptions.default(() => ManifestPollingOptions.parse({})),
    refetch: zod_1.z.number().
        default(10000).
        describe("Manifest refetch timeout for 401, 403 codes. 0 - means no re-fetch at all"),
    params: zod_1.z.record(zod_1.z.string()).default({
        substitute: "false",
        vdc: "true",
    }).describe("Additional query params for manifest requests"),
});
//#endregion
//#region Misc Options
const DriversUnion = zod_1.z.union([exports.WebrtcDriverOptions, exports.HlsjsDriverOptions, exports.NativeHlsDriverOptions, exports.Mp4WsDriverOptions, exports.FlvHttpDriverOptions]);
const PosterOptions = zod_1.z.union([
    zod_1.z.boolean(),
    zod_1.z.literal("preview"),
    zod_1.z.object({
        videoHeight: zod_1.z.number(),
        videoWidth: zod_1.z.number(),
    }),
]);
exports.PlayerDriverOptions = zod_1.z.array(DriversUnion).
    default(["webrtc", "hlsjs", "flvhttp"]).
    refine((drivers) => {
    const ids = drivers.map(d => d.id);
    return ids.length === new Set(ids).size;
}, { message: "duplicates are not allowed" }).
    describe("Request specific drivers with options");
//#endregion
exports.PlayerOptions = zod_1.z.object({
    experimental: zod_1.z.boolean().default(false).describe("Use experimental player"),
    autoPlay: zod_1.z.boolean().default(true).describe("Start playback automatically"),
    muted: zod_1.z.boolean().default(false).describe("Mute playback by default"),
    volume: zod_1.z.number().min(0).max(1).default(0.5).describe("Set initial volume"),
    displayPoster: PosterOptions.default(true).describe("Display poster before playback"),
    preferredScoreLevel: zod_1.z.nativeEnum(bitrate_switching_1.TranscodeScoreLevel).
        or(zod_1.z.nativeEnum(bitrate_switching_1.SourceScoreLevel)).
        optional().
        describe("Preferred score level for transcoded or source streams"),
    drivers: exports.PlayerDriverOptions,
    manifest: exports.ManifestOptions.default(() => exports.ManifestOptions.parse({})),
    // deprecated ones
    players: zod_1.z.array(DriversUnion).optional().superRefine(deprecated("use 'drivers' instead")),
    driverFailoverSeconds: zod_1.z.number().optional().superRefine(deprecated("use 'timeout' from driver options instead")),
    mutedAutoplayFallback: zod_1.z.boolean().optional().superRefine(deprecated("option does nothing now")),
    blurred: zod_1.z.boolean().optional().superRefine(deprecated("option does nothing now")),
    retryCall: zod_1.z.boolean().optional().superRefine(deprecated("use 'joinLoadbalancerFallback' from webrtc instead")),
    pollingInterval: zod_1.z.number().optional().superRefine(deprecated("use 'manifest.polling.default' instead")),
    notFoundPollingInterval: zod_1.z.number().optional().superRefine(deprecated("use 'manifest.polling.notfound' instead")),
    unauthorizedPollingInterval: zod_1.z.number().
        optional().
        superRefine(deprecated("use 'manifest.polling.unauthorized' instead")),
    unauthorizedRecoveryDuration: zod_1.z.number().optional().superRefine(deprecated("use 'manifest.refetch' instead")),
    refetch: zod_1.z.boolean().optional().superRefine(deprecated("use 'manifest.refetch = 0' instead")),
    urlParams: zod_1.z.record(zod_1.z.any()).optional().superRefine(deprecated("use 'manifest.params' instead")),
}).
    transform((input) => {
    //#region Deprecation Transforms
    // transform players to drivers
    if (input.players != null) {
        input.drivers = exports.PlayerDriverOptions.parse(input.players);
        delete input.players;
    }
    // transform driverFailoverSeconds to timeout
    if (input.driverFailoverSeconds != null) {
        const timeout = input.driverFailoverSeconds * 1000;
        input.drivers = input.drivers.map((d) => {
            return { ...d, timeout };
        });
        delete input.driverFailoverSeconds;
    }
    // move retryCall to webrtc driver
    if (input.retryCall != null) {
        input.drivers = input.drivers.map((d) => {
            if (d.id === "webrtc") {
                return { ...d, joinLoadbalancerFallback: Boolean(input.retryCall) };
            }
            return d;
        });
        delete input.retryCall;
    }
    delete input.mutedAutoplayFallback;
    delete input.blurred;
    // transform pollingInterval to manifest.polling.default
    if (input.pollingInterval != null) {
        input.manifest.polling.default = input.pollingInterval;
        delete input.pollingInterval;
    }
    // transform notFoundPollingInterval to manifest.polling.notfound
    if (input.notFoundPollingInterval != null) {
        input.manifest.polling.notfound = input.notFoundPollingInterval;
        delete input.notFoundPollingInterval;
    }
    // transform unauthorizedPollingInterval to manifest.polling.unauthorized
    if (input.unauthorizedPollingInterval != null) {
        input.manifest.polling.unauthorized = input.unauthorizedPollingInterval;
        delete input.unauthorizedPollingInterval;
    }
    // transform unauthorizedRecoveryDuration to manifest.refetch
    if (input.unauthorizedRecoveryDuration != null) {
        input.manifest.refetch = input.unauthorizedRecoveryDuration;
        delete input.unauthorizedRecoveryDuration;
    }
    // transform refetch to manifest.refetch = 0
    if (input.refetch === false) {
        input.manifest.refetch = 0;
        delete input.refetch;
    }
    // transform urlParams to manifest.params
    if (input.urlParams != null) {
        input.manifest.params = Object.fromEntries(Object.entries(input.urlParams).map(([k, v]) => [k, String(v)]));
        delete input.urlParams;
    }
    return input;
    //#endregion
}).
    readonly();
//# sourceMappingURL=player-options.js.map